import { type I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { PRODUCT_NAME } from 'app-shared/constants';

import { type ClerkLocalization } from '@/auth/clerk/types/ClerkLocalization';

// Clerk fills these in itself, so they pass through our messages unchanged.
const CLERK_COUNT_PLACEHOLDER = '{{count}}';
const CLERK_LIMIT_PLACEHOLDER = '{{limit}}';

// Clerk calls workspaces "organizations". Its screens people see here say
// "workspace" instead, in our own translated words.
export const getClerkLocalization = (
  clerkBaseLocalization: ClerkLocalization | undefined,
  i18n: I18n,
): ClerkLocalization => ({
  ...clerkBaseLocalization,
  // Clerk's titles name the application from its dashboard; ours always
  // name the product, whatever the Clerk application is called.
  signIn: {
    ...clerkBaseLocalization?.signIn,
    start: {
      ...clerkBaseLocalization?.signIn?.start,
      title: i18n._(msg`Sign in to ${PRODUCT_NAME}`),
    },
  },
  signUp: {
    ...clerkBaseLocalization?.signUp,
    start: {
      ...clerkBaseLocalization?.signUp?.start,
      title: i18n._(msg`Create your ${PRODUCT_NAME} account`),
    },
  },
  formFieldLabel__organizationName: i18n._(msg`Name`),
  formFieldInputPlaceholder__organizationName: i18n._(msg`Workspace name`),
  createOrganization: {
    ...clerkBaseLocalization?.createOrganization,
    title: i18n._(msg`Create workspace`),
    formButtonSubmit: i18n._(msg`Create workspace`),
  },
  organizationList: {
    ...clerkBaseLocalization?.organizationList,
    action__createOrganization: i18n._(msg`Create workspace`),
    createOrganization: i18n._(msg`Create workspace`),
    titleWithoutPersonal: i18n._(msg`Choose a workspace`),
  },
  organizationProfile: {
    ...clerkBaseLocalization?.organizationProfile,
    start: {
      ...clerkBaseLocalization?.organizationProfile?.start,
      // Missing from Clerk's French translation.
      membershipSeatUsageLabel: i18n._(
        msg`${CLERK_COUNT_PLACEHOLDER} of ${CLERK_LIMIT_PLACEHOLDER} seats used`,
      ),
    },
    invitePage: {
      ...clerkBaseLocalization?.organizationProfile?.invitePage,
      subtitle: i18n._(
        msg`Enter or paste one or more email addresses, separated by spaces or commas.`,
      ),
    },
    navbar: {
      ...clerkBaseLocalization?.organizationProfile?.navbar,
      title: i18n._(msg`Workspace`),
      description: i18n._(msg`Manage your workspace.`),
    },
  },
  organizationSwitcher: {
    ...clerkBaseLocalization?.organizationSwitcher,
    action__createOrganization: i18n._(msg`Create workspace`),
    notSelected: i18n._(msg`No workspace selected`),
  },
  taskChooseOrganization: {
    ...clerkBaseLocalization?.taskChooseOrganization,
    chooseOrganization: {
      ...clerkBaseLocalization?.taskChooseOrganization?.chooseOrganization,
      title: i18n._(msg`Choose a workspace`),
      subtitle: i18n._(msg`Join an existing workspace or create a new one`),
      subtitle__createOrganizationDisabled: i18n._(
        msg`Join an existing workspace`,
      ),
      action__createOrganization: i18n._(msg`Create a new workspace`),
    },
    createOrganization: {
      ...clerkBaseLocalization?.taskChooseOrganization?.createOrganization,
      title: i18n._(msg`Create your workspace`),
      subtitle: i18n._(
        msg`Give your team a shared space. You can invite people next.`,
      ),
      formFieldInputPlaceholder__name: i18n._(msg`My workspace`),
      formFieldInputPlaceholder__slug: i18n._(msg`my-workspace`),
    },
    organizationCreationDisabled: {
      ...clerkBaseLocalization?.taskChooseOrganization
        ?.organizationCreationDisabled,
      title: i18n._(msg`You need a workspace`),
      subtitle: i18n._(
        msg`Ask an admin of your team's workspace to invite you.`,
      ),
    },
  },
});
